import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

/* ─────────────────────────────────────────────
 * FarmlySheet – iOS-safe bottom sheet system
 *
 * Solves:
 *  1. iOS Safari keyboard pushing content off-screen
 *  2. 100vh lying on iOS (uses 100dvh with fallback)
 *  3. Body scroll leak (overscroll-behavior: contain)
 *  4. Safe-area (home indicator) padding
 *  5. CTA always reachable when keyboard is open
 *
 * Layout (flex column):
 *  ┌─ FarmlySheetHeader  (shrink-0)
 *  ├─ FarmlySheetBody    (flex-1, overflow-y-auto, focusin→scrollIntoView)
 *  └─ FarmlySheetFooter  (shrink-0, sticky, safe-area bottom padding)
 * ───────────────────────────────────────────── */

// ─── Root ─────────────────────────────────────

interface FarmlySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Side for the sheet. Default: "bottom" */
  side?: "bottom" | "right" | "left" | "top";
  /** Additional className on SheetContent */
  className?: string;
  /** Title for a11y (Radix requires it). Rendered visually when showHeader is true. */
  title?: string;
  /** Optional description below title */
  description?: string;
  /** Whether to render the default header. Default: false */
  showHeader?: boolean;
}

export function FarmlySheet({
  open,
  onOpenChange,
  children,
  side = "bottom",
  className,
  title,
  description,
  showHeader = false,
}: FarmlySheetProps) {
  // Set body attribute when bottom sheet is open to hide bottom navs globally
  useEffect(() => {
    if (side !== "bottom") return;
    if (open) {
      document.body.setAttribute("data-sheet-open", "true");
    } else {
      document.body.removeAttribute("data-sheet-open");
    }
    return () => {
      document.body.removeAttribute("data-sheet-open");
    };
  }, [open, side]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          // Core flex layout
          "flex flex-col",
          // Bottom sheet specifics
          side === "bottom" && [
            "rounded-t-2xl",
            // Use dvh for dynamic viewport height (iOS keyboard-safe)
            "max-h-[90dvh]",
            // Prevent body scroll leak
            "overscroll-behavior-contain",
          ],
          // Side panel defaults
          side === "right" && "w-full sm:max-w-md",
          className,
        )}
      >
        {/* Grabber for bottom sheets */}
        {side === "bottom" && (
          <div className="flex justify-center pt-1 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>
        )}

        {/* Visible header */}
        {showHeader && title && (
          <SheetHeader className="px-4 pb-2 shrink-0">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        )}

        {/* Hidden a11y title */}
        {!showHeader && title && (
          <SheetHeader className="sr-only">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
        )}

        {children}
      </SheetContent>
    </Sheet>
  );
}

// ─── Header ───────────────────────────────────

interface FarmlySheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export function FarmlySheetHeader({ className, children, ...props }: FarmlySheetHeaderProps) {
  return (
    <div className={cn("shrink-0 px-4 pb-2", className)} {...props}>
      {children}
    </div>
  );
}

// ─── Body (scrollable, with iOS focusin handling) ──

interface FarmlySheetBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Disable automatic scrollIntoView on focusin. Default: false */
  disableAutoScroll?: boolean;
}

export function FarmlySheetBody({
  className,
  children,
  disableAutoScroll = false,
  ...props
}: FarmlySheetBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null);

  // When an input/textarea receives focus inside the body,
  // scroll it into view within THIS container (not window).
  // Uses a small delay to let iOS keyboard animation start.
  const handleFocusIn = useCallback(
    (e: FocusEvent) => {
      if (disableAutoScroll) return;
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        // Debounce slightly so keyboard has started animating
        setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    },
    [disableAutoScroll],
  );

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.addEventListener("focusin", handleFocusIn);
    return () => el.removeEventListener("focusin", handleFocusIn);
  }, [handleFocusIn]);

  return (
    <div
      ref={bodyRef}
      className={cn(
        // Flex-1 to fill available space, scroll internally
        "flex-1 min-h-0 overflow-y-auto overscroll-contain",
        // Default padding
        "px-4 py-3",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ─── Footer (sticky, safe-area aware) ─────────

interface FarmlySheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export function FarmlySheetFooter({ className, children, ...props }: FarmlySheetFooterProps) {
  return (
    <div
      className={cn(
        "shrink-0 border-t bg-background px-4 pt-3",
        // Safe-area bottom padding for home indicator
        "farmly-sheet-footer",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
